import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import SectionCard from "../SectionCard";
import GalleryViewer from "./GalleryViewer";
import cloudinarySized from "./cloudinary_sized";

const TILE_SIZE = 160;
const TILE_GAP = 18;

/**
 * GallerySection — the shared supporting-images card (Figma's "Headshots" on expert pages, "Assets"
 * on product pages). Product and Expert both expose the same `id_images` "supporting" role as a
 * `supporting_images` jsonb column on their API views (a non-multiplying aggregate ordered by
 * `list_position`), so one display-only component serves both rather than two near-copies.
 *
 * Figma's carousel: one row of 160px tiles between previous/next buttons, which scroll a tile at a
 * time and disable at either end (on phones the row is swiped instead). Clicking a tile opens it at
 * full size in GalleryViewer.
 *
 * Tolerates the column's own absence contract: `images` arrives `null`/`undefined`/an all-empty
 * value for a profile with none, and the section renders nothing rather than an empty card. The
 * same collapse applies once every image has failed to load (an off-site thumbnail CDN going
 * down) — this component owns failure tracking itself (keyed by `image_url`, not `id`, since a
 * URL is what fails and a row whose URL changes gets a fresh chance for free) and filters failed
 * URLs out of `photos` before the empty check, so a broken tile disappears and an all-broken
 * gallery collapses the whole card rather than leaving an empty shell.
 * Renders in whatever order the aggregate already provided (the view's own ordering, not re-sorted
 * here). No upload/reorder affordance this round — that lands with the profile content editors.
 *
 * @param {Object} props
 * @param {string} props.id - DOM id for the card (system-test hook, deep links)
 * @param {Array<{ id: number|string, image_url: string }>} [props.images] - The ordered
 *   supporting-images aggregate, or null/empty when the profile has none
 * @param {string} [props.title] - Card title (default "Gallery")
 */
function GallerySection({ id, images = null, title = "Gallery" }) {
  const [failedUrls, setFailedUrls] = useState(() => new Set());
  const [originalUrls, setOriginalUrls] = useState(() => new Set());
  const [openIndex, setOpenIndex] = useState(null);
  const [scrollable, setScrollable] = useState({ back: false, forward: false });
  const stripRef = useRef(null);

  const markFailed = (url) => {
    setFailedUrls((prev) => {
      if (prev.has(url)) return prev; // already recorded -- skip the re-render
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  // A resized copy Cloudinary can't build (quota, oversized original) falls back to the original.
  const tileSrc = (url) =>
    originalUrls.has(url) ? url : cloudinarySized(url, `c_fill,w_${TILE_SIZE * 2},h_${TILE_SIZE * 2}`);
  const tileFailed = (url) => {
    if (tileSrc(url) === url) {
      markFailed(url);
    } else {
      setOriginalUrls((prev) => new Set(prev).add(url));
    }
  };

  const photos = Array.isArray(images)
    ? images.filter((image) => image && image.image_url && !failedUrls.has(image.image_url))
    : [];

  const measure = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) {
      return;
    }
    const back = strip.scrollLeft > 1;
    const forward = strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1;
    setScrollable((prev) => (prev.back === back && prev.forward === forward ? prev : { back, forward }));
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) {
      return undefined;
    }
    measure();
    strip.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(strip);
    return () => {
      strip.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [measure, photos.length]);

  if (photos.length === 0) return null;

  const scrollByTile = (direction) => {
    stripRef.current?.scrollBy({ left: direction * (TILE_SIZE + TILE_GAP), behavior: "smooth" });
  };
  const stepClasses = `btn btn-secondary gallery-step d-none d-sm-inline-flex${
    scrollable.back || scrollable.forward ? "" : " invisible"
  }`;

  return (
    <SectionCard id={id} title={title}>
      <div className="gallery-carousel d-flex align-items-center">
        <button
          type="button"
          className={`${stepClasses}${scrollable.back ? "" : " disabled"}`}
          aria-label="Scroll to previous images"
          aria-disabled={!scrollable.back || undefined}
          onClick={() => scrollByTile(-1)}
        >
          <i className="fa-solid fa-chevron-left" aria-hidden="true" />
        </button>
        <ul className="gallery-strip list-unstyled mb-0" ref={stripRef}>
          {photos.map((photo, index) => (
            <li key={photo.id}>
              <button type="button" className="gallery-tile" onClick={() => setOpenIndex(index)}>
                <ImageCropped
                  src={tileSrc(photo.image_url)}
                  alt={`${title} ${index + 1} of ${photos.length}`}
                  size={TILE_SIZE}
                  onError={() => tileFailed(photo.image_url)}
                />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={`${stepClasses}${scrollable.forward ? "" : " disabled"}`}
          aria-label="Scroll to next images"
          aria-disabled={!scrollable.forward || undefined}
          onClick={() => scrollByTile(1)}
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <GalleryViewer
        title={title}
        images={photos}
        index={openIndex !== null && openIndex < photos.length ? openIndex : null}
        onIndexChange={setOpenIndex}
        onClose={() => setOpenIndex(null)}
      />
    </SectionCard>
  );
}

GallerySection.propTypes = {
  id: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      image_url: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
};

export default GallerySection;
